import _extends from "@babel/runtime-corejs3/helpers/extends";
import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/objectWithoutProperties";
var _excluded = ["style", "className", "children", "size", "tabIndex"];
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import React, { useState, forwardRef, cloneElement, isValidElement, Children } from 'react';
import mergeRefs from 'react-merge-refs';
import Box from '@rexlabs/box';
import { useStyles, StyleSheet } from '@rexlabs/styling';
import Icons from '@rexlabs/icons';
import { useHandleOverflow } from '@rexlabs/react-hooks';
import { PrimaryTag } from './basic/primary';
var ChevronUp = Icons.ChevronUp;
var defaultStyles = StyleSheet({
  container: {
    gap: function gap(_ref) {
      var token = _ref.token;
      return token('spacing.xs');
    }
  },
  fitTag: {
    minWidth: 0
  },
  extraTag: {
    flex: '0 0 auto'
  }
});
/**
 * This component is used to group related tags together, and to limit the amount of
 * them that are visible at any one time, dealing with the overflow automatically.
 */

export var TagGroup = /*#__PURE__*/forwardRef(function TagGroup(_ref2, ref) {
  var style = _ref2.style,
      className = _ref2.className,
      children = _ref2.children,
      _ref2$size = _ref2.size,
      size = _ref2$size === void 0 ? 'm' : _ref2$size,
      _ref2$tabIndex = _ref2.tabIndex,
      tabIndex = _ref2$tabIndex === void 0 ? 0 : _ref2$tabIndex,
      props = _objectWithoutProperties(_ref2, _excluded);

  var s = useStyles(defaultStyles);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showAll = _useState2[0],
      setShowAll = _useState2[1];

  var _useHandleOverflow = useHandleOverflow({
    itemCount: Children.toArray(children).length
  }),
      containerRef = _useHandleOverflow.containerRef,
      itemRefs = _useHandleOverflow.itemRefs,
      extraCountRef = _useHandleOverflow.extraCountRef,
      visibleItemCount = _useHandleOverflow.visibleItemCount,
      extraItemCount = _useHandleOverflow.extraItemCount,
      isOverflowing = _useHandleOverflow.isOverflowing,
      recalculateLayout = _useHandleOverflow.recalculateLayout;

  return /*#__PURE__*/React.createElement(Box, _extends({}, s.with('container')({
    style: style,
    className: className
  }), {
    ref: mergeRefs([ref, containerRef])
  }, props, {
    flexWrap: showAll ? 'wrap' : 'nowrap'
  }), _mapInstanceProperty(Children).call(Children, children, function (child, index) {
    var _child$key;

    if (!showAll && !recalculateLayout && index >= visibleItemCount) {
      return null;
    }

    return /*#__PURE__*/isValidElement(child) ? /*#__PURE__*/React.createElement(Box, _extends({}, s({
      fitTag: showAll || index === 0 && isOverflowing
    }), {
      key: (_child$key = child.key) !== null && _child$key !== void 0 ? _child$key : index,
      ref: function ref(node) {
        if (node) {
          var _child$key2;

          itemRefs.current.set((_child$key2 = child.key) !== null && _child$key2 !== void 0 ? _child$key2 : index, node);
        } else {
          var _child$key3;

          itemRefs.current.delete((_child$key3 = child.key) !== null && _child$key3 !== void 0 ? _child$key3 : index);
        }
      },
      flexDirection: "row"
    }), /*#__PURE__*/cloneElement(child, {
      size: size,
      tabIndex: tabIndex
    })) : child;
  }), !recalculateLayout && extraItemCount <= 0 ? null : /*#__PURE__*/React.createElement(PrimaryTag, _extends({}, s('extraTag'), {
    ref: extraCountRef,
    isInteractive: true,
    size: size,
    onClick: function onClick() {
      return setShowAll(function (prevShowAll) {
        return !prevShowAll;
      });
    }
  }), !showAll ? "+ ".concat(extraItemCount) : /*#__PURE__*/React.createElement(Box, {
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(ChevronUp, {
    size: "s"
  }))));
});
export default TagGroup;